/* eslint-disable @typescript-eslint/no-unsafe-assignment */
import { useEffect, useState } from 'react';
import { useToast } from '@chakra-ui/react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { KeyAvailableType, PropertyKeyAvailableType } from '@acp/shared';
import { useProperty } from './use-property';
import { EditPropertyFormValues, editPropertyFormSchema, editPropertyFormSchemaValidKey } from '../components/edit-property-modal/form-schema';
import { useUpdatePropertyDetailsMutation } from '../components/edit-property-modal/use-edit-property-details-mutation';

export function useUpdatePropertyForm({
    propertyId,
    onClose,
    onUpdatedProperty,
    enforceValidKey,
    showAgencyKeyFields = true,
    showOnsiteKeyFields = true,
    isReadOnly = false,
}: {
    propertyId: number;
    onClose?: () => void;
    onUpdatedProperty?: () => void;
    enforceValidKey: boolean;
    showAgencyKeyFields?: boolean;
    showOnsiteKeyFields?: boolean;
    isReadOnly?: boolean;
}) {
    const defaultScreen = isReadOnly ? 'READ_ONLY' : 'EDIT_PROPERTY_DATA';
    const [screen, setScreen] = useState<'EDIT_PROPERTY_DATA' | 'CONFIRM' | 'READ_ONLY'>(defaultScreen);

    const { data: propertyData } = useProperty(propertyId);

    const toast = useToast();

    const editPropertyForm = useForm<EditPropertyFormValues>({
        resolver: zodResolver(enforceValidKey
            ? editPropertyFormSchemaValidKey
            : editPropertyFormSchema),
        defaultValues: {
            propertyId,
            agencyKey: {
                propertyManagerId: (propertyData?.propertyManager?.id !== undefined)
                    ? propertyData?.propertyManager?.id.toString() : '',
                isKeyAvailable: (propertyData?.isKeyAvailable === true)
                    ? KeyAvailableType.Yes : KeyAvailableType.No,
                keyNumber: propertyData?.keyNumber !== undefined ? propertyData?.keyNumber : '',
            },
            propertyKey: {
                onsiteAvailability: propertyData?.propertyKeyInformation.onsiteAvailability
                    ?? PropertyKeyAvailableType.Unavailable,
                // eslint-disable-next-line max-len
                temporaryKeyExpirationDate: propertyData?.propertyKeyInformation.temporaryKeyExpirationDate != null
                    ? new Date(propertyData?.propertyKeyInformation.temporaryKeyExpirationDate)
                    : null,
                keyOnsiteNotes: propertyData?.propertyKeyInformation.keyOnsiteNotes,
            },
        },
        mode: 'onChange',
    });

    useEffect(() => {
        if (propertyData === undefined) {
            return;
        }

        editPropertyForm.reset({
            propertyId,
            agencyKey: {
                propertyManagerId: propertyData.propertyManager?.id?.toString() ?? '',
                isKeyAvailable: propertyData.isKeyAvailable === true
                    ? KeyAvailableType.Yes
                    : KeyAvailableType.No,
                keyNumber: propertyData.keyNumber ?? '',
            },
            propertyKey: {
                onsiteAvailability: propertyData.propertyKeyInformation.onsiteAvailability
                ?? PropertyKeyAvailableType.Unavailable,
                temporaryKeyExpirationDate:
                    propertyData.propertyKeyInformation.temporaryKeyExpirationDate != null
                        ? new Date(propertyData.propertyKeyInformation.temporaryKeyExpirationDate)
                        : null,
                keyOnsiteNotes: propertyData.propertyKeyInformation.keyOnsiteNotes,
            },
        });
    }, [editPropertyForm.reset, propertyData, propertyId]);

    const handleClose = () => {
        editPropertyForm.reset();
        onClose?.();
        setScreen(isReadOnly ? 'READ_ONLY' : 'EDIT_PROPERTY_DATA');
    };

    const updatePropertyDetailsMutation = useUpdatePropertyDetailsMutation(propertyId, {
        onSuccess: () => {
            toast({
                status: 'success',
                title: 'Update property request submitted',
                description:
                    'Property details has been saved successfully',
            });

            // callback
            onUpdatedProperty?.();

            handleClose();
        },
    });

    const handleSubmit = () => {
        setScreen('CONFIRM');
    };

    const handleBack = () => {
        updatePropertyDetailsMutation.reset();
        setScreen(isReadOnly ? 'READ_ONLY' : 'EDIT_PROPERTY_DATA');
    };

    const handleConfirm = (vals: EditPropertyFormValues) => {
        if (showOnsiteKeyFields && showAgencyKeyFields
            && vals.propertyKey !== undefined
            && vals.agencyKey !== undefined
        ) {
            updatePropertyDetailsMutation.mutate({
                propertyId: vals.propertyId,
                agencyKey: {
                    isKeyAvailable: vals.agencyKey.isKeyAvailable === 'YES',
                    keyNumber: vals.agencyKey.keyNumber,
                    propertyManagerId: vals.agencyKey.propertyManagerId,
                },
                propertyKey: {
                    onsiteAvailability: vals.propertyKey.onsiteAvailability,
                    temporaryKeyExpirationDate: vals.propertyKey.temporaryKeyExpirationDate,
                    keyOnsiteNotes: vals.propertyKey.keyOnsiteNotes,
                },
            });
        }

        if (showOnsiteKeyFields
            && showAgencyKeyFields === false
            && vals.propertyKey !== undefined) {
            updatePropertyDetailsMutation.mutate({
                propertyId: vals.propertyId,
                propertyKey: {
                    onsiteAvailability: vals.propertyKey.onsiteAvailability,
                    temporaryKeyExpirationDate: vals.propertyKey.temporaryKeyExpirationDate,
                    keyOnsiteNotes: vals.propertyKey.keyOnsiteNotes,
                },
            });
        }

        if (showAgencyKeyFields
            && showOnsiteKeyFields === false
            && vals.agencyKey !== undefined) {
            updatePropertyDetailsMutation.mutate({
                propertyId: vals.propertyId,
                agencyKey: {
                    isKeyAvailable: vals.agencyKey.isKeyAvailable === 'YES',
                    keyNumber: vals.agencyKey.keyNumber,
                    propertyManagerId: vals.agencyKey.propertyManagerId,
                },
            });
        }
    };

    return {
        screen,
        editPropertyForm,
        handleSubmit,
        handleBack,
        handleClose,
        handleConfirm,
        updatePropertyDetailsMutation,
    };
}
